import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { GET_HOTEL_BOOKING_INFO, HOTEL_BOOKING } from 'store/queries';
import { Spinner } from 'admin-components/components';
import { Row, Column } from 'components/shared/Layout';
import { VerticalLine, StyledHr, LastColumn, StyledHotelDetailsContainer } from '../Extras.styles';
import { buildHotelInfoFromDotwBooking } from '../../../../utils/hotel';
import currencySymbols from 'constants/currencies';
import moment from 'moment';

const ExtrasHotelDetails = ({ hotelExternalReference, isPackage, isHotelOnly, isDotwBooking, reference }) => {
  const { data, loading } = useQuery(isDotwBooking ? HOTEL_BOOKING : GET_HOTEL_BOOKING_INFO, {
    variables: isDotwBooking
      ? {
          reference
        }
      : {
          externalReference: hotelExternalReference,
          ...((isPackage || isHotelOnly) && {
            productType: isPackage ? 'PACKAGES' : 'HOTELS'
          })
        }
  });

  let hotel = null;

  if (isDotwBooking) {
    hotel = buildHotelInfoFromDotwBooking(data?.hotelBooking);
  } else {
    hotel = data?.getHotelBooking;
  }

  const getCancellation = (room) => {
    // Step 1: Sort the array by startDate (oldest to newest)
    const sortedCancellations = [...room.cancellations].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

    // Step 2: Map to formatted strings
    const formattedCancellations = sortedCancellations.map((item) => {
      const startDate = moment(item.startDate).format('HH:mm DD/MM/YYYY');
      const endDate = moment(item.endDate).format('HH:mm DD/MM/YYYY');
      return `[${startDate} - ${endDate}] : ${item.refundPercentage}% refund - ${currencySymbols[room.currencyShort]}${item.penalty} fee`;
    });

    return formattedCancellations.join('\r\n');
  };

  const getPassengers = (room) => room.passengersDetails.map((guest) => `${guest.firstName} ${guest.lastName}`).join('\r\n');

  return (
    <StyledHotelDetailsContainer>
      {loading && (
        <Column alignItems="center" justifyContent="center">
          <Spinner />
        </Column>
      )}
      {!loading && !hotel && (
        <strong>
          <FormattedMessage id="extras.hotel.modal.hotelNotFoundError" />
        </strong>
      )}
      {!loading && hotel && (
        <>
          <Row>
            <Column span={4}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.passenger" />
                </strong>
                <div>{`${hotel.leadGuestFirstName} ${hotel.leadGuestLastName}`}</div>
              </VerticalLine>
            </Column>
            <Column span={3}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.checkInDate" />
                </strong>
                <div>{hotel.arrivalDate}</div>
              </VerticalLine>
            </Column>
            <Column span={3}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.jacTravelReference" />
                </strong>
                <div>{hotel.bookingReference}</div>
              </VerticalLine>
            </Column>
            <Column span={3}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.status" />
                </strong>
                <div>{hotel.status}</div>
              </VerticalLine>
            </Column>
            <Column span={3}>
              <LastColumn>
                <strong>
                  <FormattedMessage id="extras.hotel.eurostarPackageRef" />
                </strong>
                <div>{reference}</div>
              </LastColumn>
            </Column>
          </Row>

          <StyledHr />

          <Row>
            <Column span={4}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.hotelName" />
                </strong>
                <div>{hotel.hotelName || '-'}</div>
              </VerticalLine>
            </Column>
            <Column span={3}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.numberOfNights" />
                </strong>
                <div>{hotel.duration || '-'}</div>
              </VerticalLine>
            </Column>
            <Column span={3}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.guestCount" />
                </strong>
                <div>{hotel.numberOfGuests || '-'}</div>
              </VerticalLine>
            </Column>
            <Column span={3}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.roomCount" />
                </strong>
                <div>{hotel.numberOfRooms || '-'}</div>
              </VerticalLine>
            </Column>
            <Column span={3}>
              <LastColumn>
                <strong>
                  <FormattedMessage id="extras.hotel.hotelAddress" />
                </strong>
                <div>{hotel.address || '-'}</div>
              </LastColumn>
            </Column>
          </Row>

          <StyledHr />

          <Row>
            <Column span={4}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.rating" />
                </strong>
                <div>{hotel.rating || '-'}</div>
              </VerticalLine>
            </Column>
            <Column span={3}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.region" />
                </strong>
                <div>{hotel.region || '-'}</div>
              </VerticalLine>
            </Column>
            <Column span={3}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.resort" />
                </strong>
                <div>{hotel.resort || '-'}</div>
              </VerticalLine>
            </Column>
            <Column span={3}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.country" />
                </strong>
                <div>{hotel.country || '-'}</div>
              </VerticalLine>
            </Column>
            <Column span={3}>
              <LastColumn>
                <strong>
                  <FormattedMessage id="extras.hotel.telephone" />
                </strong>
                <div>{hotel.telephone || '-'}</div>
              </LastColumn>
            </Column>
          </Row>

          <StyledHr />

          <Row>
            <Column span={16}>
              <LastColumn>
                <strong>
                  <FormattedMessage id="extras.hotel.roomsDetails" />
                </strong>
              </LastColumn>
            </Column>
          </Row>
          {hotel.rooms.map((room, index) => (
            <Row key={`${index}_${room?.type}_${room?.numberOfGuests}`}>
              <Column span={3}>
                <LastColumn>
                  <strong>
                    <FormattedMessage id="extras.hotel.roomType" />
                  </strong>
                  <div>{room.type || '-'}</div>
                </LastColumn>
              </Column>
              <Column span={3}>
                <LastColumn>
                  <strong>
                    <FormattedMessage id="extras.hotel.guests" />
                  </strong>
                  <div style={{ whiteSpace: 'pre-line' }}>{getPassengers(room)}</div>
                </LastColumn>
              </Column>
              <Column span={3}>
                <LastColumn>
                  <strong>
                    <FormattedMessage id="extras.hotel.roomBoardBasis" />
                  </strong>
                  <div>{room.mealBasisDescription || '-'}</div>
                </LastColumn>
              </Column>
              <Column span={1}>
                <LastColumn>
                  <strong>
                    <FormattedMessage id="extras.hotel.price" />
                  </strong>
                  <div>
                    {currencySymbols[room.currencyShort]}
                    {room.totalPrice || '-'}
                  </div>
                </LastColumn>
              </Column>
              <Column span={6}>
                <LastColumn>
                  <strong>
                    <FormattedMessage id="extras.hotel.cancellation" />
                  </strong>
                  <div style={{ whiteSpace: 'pre-line' }}>{getCancellation(room)}</div>
                </LastColumn>
              </Column>
            </Row>
          ))}
        </>
      )}
    </StyledHotelDetailsContainer>
  );
};

ExtrasHotelDetails.propTypes = {
  hotelExternalReference: PropTypes.string.isRequired,
  isPackage: PropTypes.bool,
  isHotelOnly: PropTypes.bool,
  isDotwBooking: PropTypes.bool,
  reference: PropTypes.string
};

export default ExtrasHotelDetails;
